import { Eyebrow } from "../common/brand/Eyebrow";
import { ScheduledJob } from "@/types/common.types";
import useAnalyticsManager from "@/hooks/miscellaneous/useAnalyticsManager";
import { useEffect, useState } from "react";
import SearchInput from "../common/SearchInput";
import { useDebounceValue } from "usehooks-ts";
import { Button } from "../ui/button";
import Icon from "../common/Icon";
import { VisibilityState } from "@tanstack/react-table";
import MultiSelect from "../common/MultiSelectDropdown";
import { InsightsListTable } from "./table";

type JobListProps = {
  onSelectJob: (job: ScheduledJob) => void;
  onCreateJob: () => void;
};
const InsightList: React.FC<JobListProps> = ({ onSelectJob }) => {
  const { sendEvent } = useAnalyticsManager();

  sendEvent("insights_screen_viewed");

  const [search, setSearch] = useState("");
  const [debouncedSearchValue] = useDebounceValue(search, 500);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const [columnOptions, setColumnOptions] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [initialSelectedColumns, setInitialSelectedColumns] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (debouncedSearchValue) {
      sendEvent("job_search_initiated", {
        search_query: debouncedSearchValue,
        screen_name: "insights",
      });
    }
  }, [debouncedSearchValue]);
  const handleColumnChange = (selected: string[]) => {
    sendEvent("edit_columns_clicked");
    const newColumnVisibility = Object.fromEntries(
      columnOptions.map((option) => [option.id, selected.includes(option.id)])
    );
    setColumnVisibility(newColumnVisibility);
  };
  return (
    <>
      <div className="flex flex-col gap-2">
        <Eyebrow>[ INSIGHTS ]</Eyebrow>
        <h1 className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-brand-text">
          Insights.
        </h1>
        <div className="flex flex-1 items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-muted">
            Insights
          </span>
          <div className="flex items-center gap-4">
            <SearchInput value={search} onChange={setSearch} />
            <div className="flex gap-3">
              {columnOptions.length > 0 && (
                <MultiSelect
                  options={columnOptions}
                  initialSelected={initialSelectedColumns}
                  onChange={handleColumnChange}
                  trigger={
                    <Button
                      variant="outline"
                      className="flex items-center justify-center gap-2 border-brand-border font-mono text-[11px] uppercase tracking-[0.22em] text-brand-text"
                      leftIcon={<Icon name="edit" size={16} />}
                    >
                      Edit Column
                    </Button>
                  }
                  screen_name="insights"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <InsightsListTable
        onJobClick={onSelectJob}
        search={search}
        setSearch={setSearch}
        debouncedSearchValue={debouncedSearchValue}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        setColumnOptions={setColumnOptions}
        setInitialSelectedColumns={setInitialSelectedColumns}
      />
    </>
  );
};

export default InsightList;
